import { Component, HostListener, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { skip, switchMap, tap } from 'rxjs/operators';
import { DAVE_CC_PARTNERTYPE_ID, OBLIGO_PARTNERTYPE_ID } from '../../../app.component';
import { ComponentCanDeactivate } from '../../../dave-data-module/guards/pending-changes.guard';
import { State } from '../../../dave-data-module/State';
import {
    PartnerOfficeActionTypes,
    PartnersActionTypes,
} from '../../../dave-data-module/State/actions/partners.actions';
import { RoutingHistoryService } from '../../../dave-utils-module/dave-shared-components-module/services/routing-history.service';
import { bICValidator, iBANValidator, phoneNumberValidator } from '../../../helper/validation.helper';
import {
    getPartner,
    getPartnerOffices,
    getPartners,
} from '../../../dave-data-module/State/selectors/partners.selectors';

@Component({
    selector: 'app-new-partner',
    templateUrl: './new-partner.component.html',
    styleUrls: ['./new-partner.component.scss'],
})
export class NewPartnerComponent implements OnDestroy, ComponentCanDeactivate {
    readonly PartnerForm = this.fb.group({
        Name: [null],
        Email: [null, Validators.email],
        PhoneNumber: [null, phoneNumberValidator()],
        FaxNumber: null,
        Bankname: null,
        Street: null,
        PostalCode: null,
        City: null,
        Country: null,
        Iban: [null, iBANValidator()],
        Bic: [null, bICValidator()],
        PartnerNo: null,
        AutomaticPartnerNo: false,
        IsObligoPartner: null,
        UstId: null,
        Steuernummer: null,
    }) as FormGroupTyped<{
        Name: string | null;
        Email: string | null;
        PhoneNumber: string | null;
        FaxNumber: string | null;
        Bankname: string | null;
        Street: string | null;
        PostalCode: string | null;
        City: string | null;
        Country: string | null;
        Iban: string | null;
        Bic: string | null;
        PartnerNo: string | null;
        AutomaticPartnerNo: boolean;
        IsObligoPartner: boolean | null;
        UstId: string | null;
        Steuernummer: string | null;
    }>;

    public Submit$ = new Subject<void>();
    private subscriptions: Subscription[] = [];

    constructor(
        private fb: UntypedFormBuilder,
        private store: Store<State>,
        private routingHistoryService: RoutingHistoryService,
    ) {
        this.subscriptions.push(
            this.Submit$.pipe(
                switchMap(() => {
                    if (this.PartnerForm.invalid) {
                        return;
                    }
                    const form = this.PartnerForm.value;

                    this.store.dispatch(
                        PartnersActionTypes.AddPartner({
                            Payload: {
                                partner : {
                                    partnerTypeId: form.IsObligoPartner ? OBLIGO_PARTNERTYPE_ID : DAVE_CC_PARTNERTYPE_ID,
                                    partnerNo: form.PartnerNo,
                                    automaticPartnerNo: form.AutomaticPartnerNo,
                                },
                                mainOffice: {
                                    partnerId: null,
                                    name: form.Name,
                                    email: form.Email,
                                    street: form.Street,
                                    postalCode: form.PostalCode,
                                    city: form.City,
                                    phonenumber: form.PhoneNumber,
                                    faxNumber: form.FaxNumber,
                                    bankname: form.Bankname,
                                    iban: form.Iban,
                                    bic: form.Bic,
                                    country: form.Country,
                                    ustId: form.UstId,
                                    steuernummer: form.Steuernummer,
                                }
                            },
                        }),
                    );
                    return this.store.select(getPartnerOffices);
                }),
                skip(1),
                tap(() => {
                    this.navigateUp();
                })
            ).subscribe(),
            this.PartnerForm.get('AutomaticPartnerNo').valueChanges.subscribe(v => {
                if (v && this.PartnerForm.get('PartnerNo').enabled) {
                    this.PartnerForm.get('PartnerNo').disable();
                    this.PartnerForm.get('PartnerNo').setValue(null);
                } else if (!v && this.PartnerForm.get('PartnerNo').disabled) {
                    this.PartnerForm.get('PartnerNo').enable();
                }
            }),
        );
    }

    Cancel() {
        this.navigateUp();
    }

    private navigateUp() {
        this.PartnerForm.reset(this.PartnerForm.value);
        this.routingHistoryService.NavigateBackwards();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(v => v.unsubscribe());
    }
    @HostListener('window:beforeunload')
    // tslint:disable-next-line:naming-convention
    canDeactivate(): Observable<boolean> | boolean {
        return !this.PartnerForm.dirty;
    }
}
